import { type ReactNode } from "react";
import { format, isToday } from "date-fns";
import { Calendar01Icon, CheckCircleIcon, EditIcon } from "vega-icons";

import { type ShortlistItemTeaserEmailView } from "@vega-api/generated/types/shortlistItemTeaserEmailView";
import { TeaserEmailStatus } from "@vega-api/generated/types/teaserEmailStatus";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { parseUtcDate } from "@vega-core/utils/formatDate";

type DraftStatusButtonProps = {
  teaserEmail?: ShortlistItemTeaserEmailView;
  onClick: () => void;
  disabled?: boolean;
  dataTestId?: string;
};

const BASE_CLASS =
  "relative inline-flex items-center justify-center gap-1.5 h-9 min-h-7 min-w-24 shrink-0 rounded-companyBorderRadius border px-3 body-base-semibold transition-colors focus-outline-themed disabled:opacity-50 disabled:pointer-events-none";

const NEUTRAL_CLASS =
  "border-border/primary text-text/primary hover:bg-background/component";

const ICON_CLASS = "size-4";

type StatusVariant = {
  className: string;
  icon: ReactNode;
  label: string;
  dotClassName?: string;
  scheduledAt?: string;
};

const getVariant = (
  teaserEmail: ShortlistItemTeaserEmailView | undefined,
): StatusVariant => {
  if (teaserEmail?.status === TeaserEmailStatus.SENT) {
    return {
      className: NEUTRAL_CLASS,
      icon: <CheckCircleIcon className={ICON_CLASS} />,
      label: m.coInvestments_draftEmail_sentButton(),
    };
  }

  if (teaserEmail?.status === TeaserEmailStatus.SCHEDULED) {
    return {
      className: "border-transparent bg-blue-600 text-white hover:bg-blue-700",
      icon: <Calendar01Icon className={ICON_CLASS} />,
      label: m.coInvestments_draftEmail_scheduledButton(),
      dotClassName: "bg-blue-300",
      scheduledAt: teaserEmail.scheduledAt,
    };
  }

  if (teaserEmail?.status === TeaserEmailStatus.DRAFT) {
    return {
      className:
        "border-transparent bg-amber-500 text-white hover:bg-amber-600",
      icon: <EditIcon className={ICON_CLASS} />,
      label: m.coInvestments_draftEmail_draftButton(),
      dotClassName: "bg-amber-300",
    };
  }

  return {
    className: NEUTRAL_CLASS,
    icon: <EditIcon className={ICON_CLASS} />,
    label: m.coInvestments_draftEmail_draftButton(),
  };
};

// Intentionally not useDateUtils().customFormatRelativeDate — that includes the
// year ("MMM d, yyyy 'at' h:mm a"); this drops it to keep the button label short.
export const formatScheduledLabel = (date: Date): string =>
  isToday(date)
    ? m.dates_todayFormatted({ date: format(date, "p") })
    : format(date, "MMM d, p");

export function DraftStatusButton({
  teaserEmail,
  onClick,
  disabled = false,
  dataTestId,
}: DraftStatusButtonProps) {
  const variant = getVariant(teaserEmail);

  // The aria-label carries the "scheduled" meaning the visible send-time label doesn't.
  const scheduledLabel = variant.scheduledAt
    ? formatScheduledLabel(parseUtcDate(variant.scheduledAt))
    : null;

  const ariaLabel = scheduledLabel
    ? m.coInvestments_draftEmail_scheduledAriaLabel({
        dateTime: scheduledLabel,
      })
    : undefined;

  return (
    <button
      aria-label={ariaLabel}
      className={cnTwMerge(BASE_CLASS, variant.className)}
      data-testid={dataTestId}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {variant.icon}
      {scheduledLabel ?? variant.label}
      {variant.dotClassName ? (
        <span
          className={cnTwMerge(
            "absolute -right-1 -top-1 size-2 rounded-full ring-2 ring-background/card",
            variant.dotClassName,
          )}
        />
      ) : null}
    </button>
  );
}
